/** Admin-only queue for Ghost Organizations.
 *
 *  When a Staff / Corporate-rep user signs up and their parent company
 *  isn't in the platform yet, we auto-create a "ghost" organization row
 *  with `status: PENDING_VERIFICATION` and lock the org until an admin
 *  verifies. This page lists every such pending row so admins can
 *  approve (unlock org + all its members) or reject (flag it) with an
 *  optional note.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Building2, User2, Mail, Phone, Layers, RefreshCw, Send } from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;

const ORG_TYPE_LABEL = {
  association:         "Trade Org",
  production_house:    "Production House",
  studio:              "Studio",
  vendor:              "Vendor",
  investor:            "Investor",
  studio_producer:     "Studio-Producer",
  advertising_agency:  "Advertising Agency",
  studio_network:      "Studio · Network",
  event_production:    "Event Production",
  pr_agency:           "PR Agency",
  casting_agency:      "Casting Agency",
};

export default function AdminPendingOrgs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({});

  const isAdmin = user?.is_admin
    || (user?.email && user?.email === "admin@callsheet.app");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pending-orgs`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load pending orgs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const decide = async (id, action) => {
    try {
      await axios.post(`${API}/admin/pending-orgs/${id}/decision`, {
        action, note: note[id] || "",
      });
      toast.success(
        action === "approve"
          ? "Approved — org and members unlocked."
          : "Rejected."
      );
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center"
           data-testid="admin-pending-orgs-forbidden">
        <h1 className="font-display text-3xl uppercase">Not Authorized</h1>
        <p className="text-sm text-white/60 mt-3">Admin credentials required.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="admin-pending-orgs-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Admin · Verification Queue
          </div>
          <h1 className="font-display text-4xl uppercase tracking-tight mt-2">
            Ghost Organizations
          </h1>
          <p className="mt-2 text-sm text-white/60 max-w-3xl">
            Auto-created during staff / corporate-rep signup. Approve to unlock
            the org + every bound user, or reject to keep it locked.
          </p>
        </div>
        <Button variant="outline" onClick={load}
                className="h-9 px-4 text-xs uppercase tracking-[0.16em] border-white/20"
                data-testid="admin-pending-orgs-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-quick-links">
        <Link to="/admin/affiliations" data-testid="admin-link-affiliations"
              className="inline-flex items-center gap-2 rounded-full border border-[#F5B82E]/40 px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] text-[#F5B82E] hover:bg-[#F5B82E]/10 transition">
          <Layers size={12} /> Affiliation Editor
        </Link>
        <button
          data-testid="admin-send-digest-now"
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/ghost-org-digest/send-now");
              if (data.dry_run) {
                toast.info(`DRY-RUN: ${data.new_count} new, ${data.total_pending} total. Recipients: ${(data.recipients || []).join(", ")}. Add RESEND_API_KEY to send.`);
              } else if (data.error) {
                toast.error(`Send failed: ${data.error}`);
              } else if (data.id) {
                toast.success(`Digest sent to ${(data.recipients || []).length} recipient(s)`);
              } else {
                toast.info("Digest processed");
              }
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Trigger failed");
            }
          }}
          className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] text-emerald-300 hover:bg-emerald-500/10 transition"
        >
          <Send size={12} /> Send Digest Now
        </button>
      </div>

      <div className="mt-8 space-y-3" data-testid="admin-pending-orgs-list">
        {loading && (
          <div className="text-sm text-white/60"
               data-testid="admin-pending-orgs-loading">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="cs-card rounded-2xl p-8 text-center"
               data-testid="admin-pending-orgs-empty">
            <ShieldCheck size={24} className="text-emerald-400 mx-auto mb-2" />
            <div className="text-sm text-white/70">
              Nothing pending. Ghost orgs will appear here when staff sign up
              for companies not yet on the platform.
            </div>
          </div>
        )}
        {items.map((org) => (
          <div key={org.id}
               className="cs-card rounded-2xl p-5"
               data-testid={`admin-pending-org-row-${org.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/10 text-white/80 shrink-0">
                <Building2 size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-display text-xl">
                    {org.brand_name || org.legal_name}
                  </div>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 px-2 py-0.5 rounded-full border border-white/15">
                    {ORG_TYPE_LABEL[org.type] || org.type}
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/25 bg-amber-500/[0.06]">
                    Pending
                  </span>
                </div>
                <div className="text-xs text-white/55 mt-1">
                  Legal: <span className="text-white/75">{org.legal_name}</span>
                </div>
                <div className="text-[11px] text-white/45 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1">
                    <Layers size={12} /> {org.member_count} member(s) waiting
                  </span>
                  <span>· Created {new Date(org.created_at).toLocaleString()}</span>
                </div>
                {org.creator && (
                  <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-1.5">
                      Created By
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/75">
                      <span className="flex items-center gap-1.5">
                        <User2 size={12} /> {org.creator.name}
                        {org.creator.designation && (
                          <span className="text-white/50"> · {org.creator.designation}</span>
                        )}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Mail size={12} /> {org.creator.email}
                      </span>
                      {org.creator.phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone size={12} /> {org.creator.phone}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex gap-2 flex-wrap">
                  <input
                    type="text"
                    value={note[org.id] || ""}
                    onChange={(e) => setNote({ ...note, [org.id]: e.target.value })}
                    placeholder="Optional note (stored on the decision)"
                    className="flex-1 min-w-[200px] bg-black/40 border border-white/15 rounded-lg h-9 px-3 text-xs"
                    data-testid={`admin-pending-org-note-${org.id}`}
                  />
                  <Button onClick={() => decide(org.id, "approve")}
                          className="h-9 px-4 text-xs uppercase tracking-[0.16em]"
                          data-testid={`admin-pending-org-approve-${org.id}`}>
                    <ShieldCheck size={14} className="mr-1.5" /> Approve
                  </Button>
                  <Button onClick={() => decide(org.id, "reject")} variant="outline"
                          className="h-9 px-4 text-xs uppercase tracking-[0.16em] border-white/20"
                          data-testid={`admin-pending-org-reject-${org.id}`}>
                    <ShieldOff size={14} className="mr-1.5" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
